import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'

import { useAuthStore } from '@hooks/useAuthStore'
import { resolveApiClientBaseUrl } from '@lib/runtime-url'
import { getOrCreateTelemetrySession } from '@lib/telemetrySession'
import { clinicalRequestScope } from '@lib/clinicalSession'

type SessionRequestConfig = AxiosRequestConfig & { _retry?: boolean; _clinicalSessionScope?: string }
const sessionChanged = (config?: SessionRequestConfig) =>
  config?._clinicalSessionScope !== undefined && config._clinicalSessionScope !== clinicalRequestScope()

// Default to same-origin proxying, but allow an explicit backend URL for local dev.
export const API_BASE_URL = resolveApiClientBaseUrl(import.meta.env.VITE_API_URL)

type ApiErrorPayload =
  | string
  | {
      detail?: string | Array<{ msg?: string }>
      message?: string
      code?: string
    }

const extractErrorDetail = (payload: ApiErrorPayload | undefined): string | undefined => {
  if (!payload) return undefined

  if (typeof payload === 'string') {
    return payload
  }

  if (Array.isArray(payload.detail)) {
    return payload.detail.map((item) => item?.msg).filter(Boolean).join('. ')
  }

  return payload.detail || payload.message
}

const translateBackendMessage = (message?: string): string | undefined => {
  if (!message) return undefined

  const normalized = message.trim()
  const lower = normalized.toLowerCase()

  if (lower.includes('database error occurred')) {
    return 'No pudimos completar la operación porque el sistema está ocupado en este momento. Inténtalo nuevamente en unos minutos.'
  }

  if (lower.includes('data integrity error')) {
    return 'No fue posible guardar la información. Revisa los datos capturados e inténtalo de nuevo.'
  }

  if (lower.includes('network connectivity error')) {
    return 'No pudimos comunicarnos con el servidor. Verifica la conexión e inténtalo otra vez.'
  }

  if (lower.includes('timed out')) {
    return 'La solicitud tardó demasiado en responder. Inténtalo nuevamente.'
  }

  if (lower.includes('unexpected error occurred')) {
    return 'Ocurrió un problema inesperado. Por favor, inténtalo más tarde.'
  }

  if (lower.includes('appointment not found')) {
    return 'No encontramos la cita seleccionada.'
  }

  if (lower.includes('appointment does not belong to the active clinic')) {
    return 'La cita no pertenece a la clínica activa.'
  }

  if (lower.includes('follow-up appointments require an origin_consultation_id')) {
    return 'Para programar un seguimiento debes seleccionar la consulta previa que le da origen.'
  }

  if (lower.includes('origin_consultation_id is only allowed for follow-up appointments')) {
    return 'La consulta de origen solo aplica cuando el tipo de cita es seguimiento.'
  }

  if (lower.includes('origin consultation not found')) {
    return 'No encontramos la consulta origen seleccionada.'
  }

  if (lower.includes('follow-up appointment must reference a completed consultation for the same patient')) {
    return 'El seguimiento debe vincularse a una consulta completada del mismo paciente.'
  }

  if (lower.includes('follow-up appointment must reference a completed consultation from the active clinic')) {
    return 'El seguimiento debe vincularse a una consulta completada de la clínica activa.'
  }

  if (lower.includes('follow-up appointment must reference a completed consultation')) {
    return 'El seguimiento debe vincularse a una consulta previa ya completada.'
  }

  if (lower.includes('appointment type') && lower.includes('cannot start a consultation')) {
    return 'Ese tipo de cita no puede iniciar una consulta clínica.'
  }

  if (
    lower.includes('cannot start consultation for appointment with status') &&
    lower.includes('appointment must be confirmed or checked in')
  ) {
    return 'No se puede iniciar la consulta porque la cita aún está programada. Primero debes marcarla como confirmada o registrar la llegada del paciente.'
  }

  if (lower.includes("cannot change appointment status from 'scheduled' to 'completed'")) {
    return 'No puedes marcar una cita programada como completada directamente. Primero debe avanzar a consulta en curso.'
  }

  if (lower.includes('cannot change appointment status from')) {
    return 'El cambio de estado solicitado no está permitido para esta cita.'
  }

  if (lower.includes('cannot set appointment status to')) {
    return 'Tu rol no tiene permiso para cambiar la cita a ese estado.'
  }

  if (lower.includes('this appointment already has a consultation')) {
    return 'Esta cita ya tiene una consulta registrada.'
  }

  if (lower.includes('consultation not found')) {
    return 'No encontramos la consulta solicitada.'
  }

  if (lower.includes('consultation is locked')) {
    return 'La consulta está bloqueada. Solicita un desbloqueo para continuar.'
  }

  if (lower.includes('only the authoring doctor or an admin can edit this consultation')) {
    return 'Solo el médico que creó la consulta o un administrador pueden editarla.'
  }

  if (lower.includes('at least one icd-10 diagnosis code is required')) {
    return 'Debes capturar al menos un código CIE-10 para completar la consulta.'
  }

  return normalized
}

export const getApiErrorMessage = (
  error: AxiosError,
  fallback = 'No pudimos completar la solicitud.'
): string => {
  const detail = translateBackendMessage(
    extractErrorDetail(error.response?.data as ApiErrorPayload | undefined)
  )

  if (detail) {
    return detail
  }

  if (error.response?.status === 503) {
    return 'El servicio no está disponible en este momento. Inténtalo nuevamente en unos minutos.'
  }

  if (error.response?.status === 500) {
    return 'Ocurrió un problema interno al procesar la solicitud. Inténtalo más tarde.'
  }

  if (error.response?.status === 403) {
    return 'No tienes permiso para realizar esta acción o acceder a este recurso.'
  }

  if (error.code === 'ECONNABORTED') {
    return 'La solicitud tardó demasiado en responder. Inténtalo de nuevo.'
  }

  if (!error.response) {
    return 'No pudimos comunicarnos con el servidor. Esto puede deberse a una caída temporal del servicio o a una restricción de conexión entre el portal y la API.'
  }

  return fallback
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Once a session is terminated (explicit logout or an unrecoverable 401) we set
// this flag so no in-flight request can refresh the token and resurrect the
// session before the hard redirect tears the page down. It resets to false on
// the next full page load (a fresh JS context), so logging in again works.
let sessionTerminated = false

/**
 * Tear the session down completely and hard-redirect to /login.
 *
 * A hard redirect (not SPA navigate) is intentional: it aborts every in-flight
 * request, closes SSE/sockets, clears timers and the React Query cache, and
 * boots a fresh app from cleared storage — so nothing can auto-log the user
 * back in (no server-side refresh-token revocation exists).
 */
const terminateSession = (reason?: string) => {
  useAuthStore.getState().logout()
  // Belt-and-suspenders: ensure the persisted auth store is purged.
  try {
    ;(useAuthStore as unknown as { persist?: { clearStorage?: () => void } }).persist?.clearStorage?.()
  } catch { /* ignore */ }
  try {
    localStorage.removeItem('hms-auth-storage')
  } catch { /* ignore */ }

  // Already on /login: just clean up. Do NOT set the terminated flag, or the
  // upcoming login request would be blocked by the request interceptor.
  if (window.location.pathname === '/login') return

  // Block all further requests until the hard redirect tears the page down.
  sessionTerminated = true

  const params = new URLSearchParams()
  if (reason) params.set('reason', reason)
  if (reason) {
    const next = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (next && next !== '/') params.set('next', next)
  }
  const query = params.toString()
  window.location.href = query ? `/login?${query}` : '/login'
}

/** Explicit user logout — hard, non-resurrectable. */
export const performLogout = () => terminateSession()

const redirectToLogin = () => terminateSession('session-expired')

// Single-flight refresh: concurrent 401s share one /auth/refresh call so the
// rotating refresh token isn't spent twice (which logged users out at random).
let refreshPromise: Promise<string> | null = null
let refreshScope: string | null = null

const refreshAccessToken = (refreshToken: string): Promise<string> => {
  const scope = clinicalRequestScope()
  if (!refreshPromise || refreshScope !== scope) {
    refreshScope = scope
    const pending = apiClient
      .post('/auth/refresh', { refresh_token: refreshToken })
      .then((response) => {
        if (clinicalRequestScope() !== scope) throw new Error('Clinical session changed')
        const { access_token, refresh_token } = response.data
        useAuthStore.getState().setTokens(access_token, refresh_token)
        return access_token as string
      })
      .finally(() => {
        if (refreshPromise === pending) {
          refreshPromise = null
          refreshScope = null
        }
      })
    refreshPromise = pending
  }
  return refreshPromise
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // After logout/termination, refuse to send anything — the page is tearing
    // down. Prevents a late request from triggering a token refresh.
    if (sessionTerminated) {
      throw new Error('Session terminated')
    }

    if (sessionChanged(config as SessionRequestConfig)) throw new Error('Clinical session changed')
    ;(config as SessionRequestConfig)._clinicalSessionScope = clinicalRequestScope()

    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    if (!config.headers['X-Request-ID']) {
      config.headers['X-Request-ID'] = crypto.randomUUID()
    }
    // Correlate this request with the browser telemetry session.
    config.headers['X-App-Session-Id'] = getOrCreateTelemetrySession().id

    return config
  },
  (error) => {
    throw error
  },
  // Capture credentials before a clinic switch can run in the next microtask.
  { synchronous: true }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    if (sessionChanged(response.config as SessionRequestConfig)) throw new Error('Clinical session changed')
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as SessionRequestConfig
    if (sessionChanged(originalRequest)) throw new Error('Clinical session changed')
    const status = error.response?.status
    const requestUrl = originalRequest?.url ?? ''
    const isRefreshCall = requestUrl.includes('/auth/refresh')

    // Only 401 should invalidate the session. A 403 means the user is
    // authenticated but not authorized for the specific action/scope.

    // Handle 401 Unauthorized. If refresh is unavailable or also fails,
    // immediately redirect to login because the session is no longer valid.
    if (status === 401) {
      const refreshToken = useAuthStore.getState().refreshToken

      // Never refresh after the session was terminated (logout/expiry).
      if (sessionTerminated || isRefreshCall || originalRequest._retry || !refreshToken) {
        if (!sessionTerminated) redirectToLogin()
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        const access_token = await refreshAccessToken(refreshToken)
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
        }
        return apiClient(originalRequest)
      } catch {
        if (!sessionChanged(originalRequest)) redirectToLogin()
        return Promise.reject(error)
      }
    }
    
    // Log actual API error details so server-side errors are visible in the
    // console instead of being masked by the browser's generic CORS warning.
    const humanMessage = getApiErrorMessage(error)
    error.message = humanMessage

    if (error.response) {
      console.error(
        `[API] ${error.response.status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
        {
          humanMessage,
          data: error.response.data,
        },
      )
    } else {
      console.error(`[API] Error de red — ${humanMessage}`, error.config?.url)
    }

    return Promise.reject(error)
  }
)

// Generic API methods
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then((res) => res.data),
    
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then((res) => res.data),
    
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then((res) => res.data),
    
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then((res) => res.data),
    
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then((res) => res.data),
}

export default apiClient
